<script setup lang="ts">
import type { FileUpload } from "@profile-service-types/index.ts";
import { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import { computed, inject, ref, toRefs, unref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import JSONtoHTML from "@/components/forms/SharedComponents/WYSIWYG/JSONtoHTML.vue";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import DocumentView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/DocumentView/index.vue";
import JsonTreeView from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/JsonTreeView/index.vue";
import type {
  SubmissionCurrentProps,
  SubmissionFileOverride,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/types";
import {
  formatDate,
  getSubmissionDocuments,
  getSubmissionJsonData,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/utils.ts";
import { TODO_SUBMISSION_STATUS_KEY } from "@/components/MyStudents/Submissions/composables/useTodoSubmissionStatus";
import { useUpdateSubmissionFeedback } from "@/components/MyStudents/Submissions/composables/useUpdateSubmissionFeedback";
import { ARCHIVED_TOOLTIP_MESSAGE } from "@/components/MyStudents/Submissions/constants";
import AddSmallIcon from "@/components/shared/icons/AddSmallIcon.vue";
import ArrowDown from "@/components/shared/icons/ArrowDown.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import EditIcon from "@/components/shared/icons/EditIcon.vue";
import { hasTodoUpdateSubmissionPermission } from "@/composables/authorization.js";
import { ERROR_TIMEOUT, MAX_NOTE_TEXTAREA_LIMIT, SUCCESS_TIMEOUT } from "@/constants.js";
import { hasTiptapText } from "@/util/tiptap";

const toast = useToast();

const SAVED_FEEDBACK_DISPLAY_MS = 1200;

const props = defineProps<SubmissionCurrentProps>();
const { isTodoArchived, submission } = toRefs(props);

const emit = defineEmits<{
  (e: "open-preview", submission: SubmissionCurrentProps["submission"], file: SubmissionFileOverride): void;
}>();

const openPreview = (file: SubmissionFileOverride): void => {
  emit("open-preview", submission.value, file);
};

const feedback = ref(submission.value?.feedback ? structuredClone(submission.value.feedback) : undefined);
const collapseInformationRef = ref(true);
const isFeedbackInEditMode = ref(false);
const justSaved = ref(false);

const { mutateAsync: updateSubmissionFeedback, isPending: isSaving } = useUpdateSubmissionFeedback();

const todoSubmissionStatus = inject(TODO_SUBMISSION_STATUS_KEY, undefined);

const requiredFeedbackMode = computed<boolean>(() => !!todoSubmissionStatus?.requiredFeedbackMode.value);
const feedbackHasText = computed<boolean>(() => hasTiptapText(feedback.value));

watch(requiredFeedbackMode, (required) => {
  if (required) {
    setFeedbackEditMode(true);
  }
});

const submissionData = computed(() => submission.value?.submission_data);
const submissionJsonData = computed(() => getSubmissionJsonData(submissionData.value) as Record<string, unknown>);
const submissionDocuments = computed(() => getSubmissionDocuments(submissionData.value) as FileUpload[]);
const reviewer = computed(() => submission.value?.reviewer);

const isButtonDisabled = computed(
  () => isSaving.value || justSaved.value || !hasTodoUpdateSubmissionPermission.value || isTodoArchived.value,
);

const archivedTooltipConfig = computed(() => ({
  value: ARCHIVED_TOOLTIP_MESSAGE,
  disabled: !isTodoArchived.value,
  escape: true,
  id: "duration-tooltip",
  class: "bg-white max-w-xs shaddow rounded white",
  hideDelay: "0.3",
  pt: {
    text: "text-indigo-base p-3 leading-none",
  },
}));

// The dropdown persists status through the override table, so the raw
// submission status no longer tracks the advisor's decision — key the issue
// treatment off the derived status instead.
const feedbackTextColorClass = computed(() =>
  todoSubmissionStatus?.displayedStatus.value === TodosSubmissionsStatus.RequiresAChange
    ? "text-error-900"
    : "text-indigo-base",
);

const hasSubmissionContent = computed(
  () => submissionDocuments.value.length > 0 || Object.keys(submissionJsonData.value).length > 0,
);

const collapseInformation = (): void => {
  collapseInformationRef.value = !collapseInformationRef.value;
};
const setFeedbackEditMode = (status: boolean): void => {
  isFeedbackInEditMode.value = status;
};

const saveFeedback = async (): Promise<void> => {
  await updateSubmission();
  setFeedbackEditMode(false);
};
const cancelFeedback = (): void => {
  feedback.value = submission.value?.feedback ? structuredClone(submission.value.feedback) : undefined;
  todoSubmissionStatus?.revert();
  setFeedbackEditMode(false);
};
const persistFeedback = async (): Promise<boolean> => {
  if (todoSubmissionStatus?.pendingStatusChange.value) {
    return await todoSubmissionStatus.confirm(feedback.value);
  }
  await updateSubmissionFeedback({
    submissionId: submission.value.id,
    status: submission.value.status,
    feedback: feedback.value,
  });
  return true;
};
const updateSubmission = async (): Promise<void> => {
  try {
    if (hasTodoUpdateSubmissionPermission.value) {
      const succeeded = await persistFeedback();
      if (succeeded) {
        toast.open({
          message: "Submission has been updated successfully!",
          type: "success",
          position: "bottom",
          duration: SUCCESS_TIMEOUT,
        });
        justSaved.value = true;
        await new Promise((resolve) => setTimeout(resolve, SAVED_FEEDBACK_DISPLAY_MS));
        justSaved.value = false;
        setFeedbackEditMode(false);
      }
    }
  } catch {
    toast.open({
      message: "Error while updating Submission. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <div class="bg-white shadow-md rounded-lg p-6 w-full text-indigo-base" data-testid="my-students-submission-current-section">
    <div
      class="flex md:flex-row flex-col pb-4 border-gray-200 border-b-2"
      data-testid="my-students-submission-current-header"
    >
      <div class="flex flex-row w-full items-center cursor-pointer gap-3" @click.prevent="collapseInformation">
        <div class="flex items-center justify-center w-7 h-7">
          <ArrowDown v-if="collapseInformationRef" data-testid="my-students-submission-current-arrow-down" />
          <ArrowRight v-else class="w-5 h-5" data-testid="my-students-submission-current-arrow-right" />
        </div>
        <div class="flex flex-col justify-center">
          <div class="font-bold text-base">
            Most Recent Submission
          </div>
          <div class="text-xs" data-testid="my-students-submission-current-submitted-date">
            Submitted on <span class="font-bold">{{formatDate(submission?.created_at)}}</span>
          </div>
        </div>
      </div>
    </div>
    <Transition
      enter-active-class="transition-all duration-100 ease-out"
      enter-from-class="translate-y-1"
      enter-to-class="translate-y-0"
      leave-active-class="transition-all duration-100 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="translate-y-1"
    >
      <div
        v-show="collapseInformationRef"
        class="grid gap-10 items-start pt-4"
        :class="hasSubmissionContent ? 'grid-cols-[382px_1fr] max-md:grid-cols-1' : 'grid-cols-1'"
        data-testid="my-students-submission-current-body"
      >
        <div
          v-if="hasSubmissionContent"
          class="flex flex-col justify-center w-full gap-5"
        >
          <div v-if="submissionDocuments.length">
            <DocumentView
              :documents="submissionDocuments"
              :submission-id="submission.id"
              @open-preview="openPreview"
            />
          </div>
          <JsonTreeView v-if="Object.keys(submissionJsonData).length > 0" :data="submissionJsonData" class="w-full"  />
        </div>
        <div class="flex flex-col gap-3.5 min-w-0">
          <div class="flex items-center justify-between gap-3 rounded border border-gray-175 px-4 py-2.5">
            <span class="text-base font-bold text-indigo-base">Feedback</span>
            <span v-if="feedback && !isFeedbackInEditMode" v-tooltip.top="archivedTooltipConfig">
              <button
                class="inline-flex  h-7 items-center gap-1.5 rounded border border-teal-900 bg-transparent px-3 text-xs font-bold uppercase tracking-[0.5px] text-teal-900 transition-colors duration-150 hover:bg-teal-light disabled:cursor-not-allowed disabled:border-gray-175 disabled:text-gray-500 disabled:opacity-70 disabled:hover:bg-transparent"
                type="button"
                data-testid="my-students-submission-current-edit-feedback-button"
                :disabled="isButtonDisabled"
                @click="setFeedbackEditMode(true)"
              >
                <EditIcon class="w-3 h-3"/>
                Edit
              </button>
            </span>
          </div>

          <div
            v-if="!feedback && !isFeedbackInEditMode"
            class="flex flex-col items-center gap-2.5 rounded-md border-2 border-dashed border-gray-475 bg-gray-50 p-5 text-center text-13p text-gray-800"
            data-testid="my-students-submission-current-feedback-empty-state"
          >
            No feedback yet.
            <span v-tooltip.top="archivedTooltipConfig">
              <button
                type="button"
                class="inline-flex h-8 items-center gap-1.5 rounded bg-teal-900 px-3.5 text-xs font-bold uppercase tracking-[0.5px] text-white transition-colors duration-150 hover:bg-[#006667] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 disabled:hover:bg-gray-300"
                data-testid="my-students-submission-current-add-feedback-button"
                :disabled="isButtonDisabled"
                @click="setFeedbackEditMode(true)"
              >
                <AddSmallIcon class="w-3.5 h-3.5" />
                Add feedback
              </button>
            </span>
          </div>
          <div v-if="!isFeedbackInEditMode" class="flex flex-1 flex-col pt-2">
            <div class="flex flex-1">
              <JSONtoHTML
                v-if="feedback"
                :tiptap-json="feedback"
                :custom-class="`text-13p leading-[1.55] ${feedbackTextColorClass}`"
                data-testid="my-students-submission-current-feedback-content"
              />
            </div>
            <div class="flex flex-1 md:flex-row flex-col pt-2">
              <div class="flex flex-1">
                <span
                  v-if="submission?.reviewer_user_id"
                  class="text-sm"
                  data-testid="my-students-submission-current-feedback-reviewer"
                >
                  Added by {{ reviewer?.first_name }} {{ reviewer?.last_name }} (<span class="text-teal-900">{{ reviewer?.email }}</span>)
                </span>
              </div>
              <div class="flex text-sm" data-testid="my-students-submission-current-feedback-date">
                {{ formatDate(submission?.reviewed_at) }}
              </div>
            </div>
          </div>
          <div v-else class="flex flex-1 flex-col w-full">
            <div
              v-if="requiredFeedbackMode"
              class="flex items-start gap-2 rounded-md border border-red-300 bg-error-100 px-3 py-2.5 mb-2.5 text-13p font-semibold leading-snug text-error-900"
              data-testid="my-students-feedback-required-notice"
            >
              <span>Feedback is required when marking a submission as <b>Not Accepted</b>. Let the student know what needs to be updated.</span>
            </div>
            <div class="flex flex-1 w-full" :class="requiredFeedbackMode ? '[&_.editor]:!border-error-900' : ''">
              <WYSIWYG
                v-model="feedback"
                class="!text-xs"
                data-testid="my-students-submission-current-edit-wysiwyg-feedback"
                :editable="!isSaving || hasTodoUpdateSubmissionPermission"
                :character-limit="MAX_NOTE_TEXTAREA_LIMIT"
                :show-limit="true"
              />
            </div>
            <div class="flex items-center gap-2.5 pt-4">
              <span class="mr-auto text-xs text-gray-600">Visible to the student in their To-Dos.</span>
              <button
                type="button"
                class="h-[34px] rounded border border-gray-475 bg-transparent px-4 text-xs font-bold uppercase tracking-[0.5px] text-indigo-base transition-colors duration-150 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-70"
                data-testid="my-students-submission-current-edit-cancel-feedback-button"
                :disabled="isSaving || justSaved"
                @click="cancelFeedback"
              >
                Cancel
              </button>
              <ButtonWithSpinner
                :prop-loading="isSaving"
                :disabled="justSaved || (requiredFeedbackMode && !feedbackHasText)"
                :data-test-id="`review-submissions-submission-current-edit-save-feedback-button`"
                variant="secondary"
                button-height="!h-[34px]"
                custom-class="!mr-0 !rounded !px-4 hover:!bg-[#006667]"
                @click="saveFeedback"
              >
                <span class="text-xs font-bold uppercase tracking-[0.5px]">{{ justSaved ? "Saved ✓" : "Save Feedback" }}</span>
              </ButtonWithSpinner>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
